import React, { useEffect, useState } from "react";
import { FaSearch, FaCalendarAlt, FaLink, FaLayerGroup } from "react-icons/fa";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./AdminDateEditor.css";
import AdminAuditReport from "./AdminAuditReport";
const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

export default function AdminDateEditor() {
  const [searchId, setSearchId] = useState("");
  const [relatedTransactions, setRelatedTransactions] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [transactions, setTransactions] = useState([]); // For audit report link
  useEffect(() => {
    // Fetch all transactions on component mount for audit report access
    const fetchAllTransactions = async () => {
      try {
        const res = await fetch(API_URL);
        const data = await res.json();
        setTransactions(data.transactions || []);
      } catch (err) {
        console.error("Failed to fetch transactions for audit report:", err);
      }
    };
    fetchAllTransactions();
  }, []);
  // 1. Search for all connected transactions
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchId || searchId.length < 13) {
      alert("Please enter a valid Transaction ID (Like: TX-1234567890).");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const all = data.transactions || [];
      // Store all transactions for audit report access
      // Extract the Base ID (Removing suffixes like -IN, -OUT, -PART-0)
      const baseId = searchId.split("-").slice(0, 2).join("-");

      // Find all transactions that share this Base ID or are linked
      const family = all.filter(
        (tx) => tx.paymentId.includes(baseId) || tx.linkedId?.includes(baseId),
      );

      if (family.length === 0) {
        setMessage({
          text: "No transactions found with that ID.",
          type: "error",
        });
      } else {
        setRelatedTransactions(family);
        setMessage({
          text: `Found ${family.length} connected records.`,
          type: "success",
        });
      }
    } catch (err) {
      setMessage({ text: "Search failed.", type: "error" });
    }
    setLoading(false);
  };

  // 2. Bulk Update Dates
  const handleUpdateDate = async () => {
    if (!newDate || relatedTransactions.length === 0) return;

    const confirmMsg = `This will change the date for ${relatedTransactions.length} records. Continue?`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      // We process updates sequentially to ensure DB consistency
      for (const tx of relatedTransactions) {
        // Create new timestamp keeping the original time but changing the date
        const originalTime = tx.timestamp.split("T")[1];
        const updatedTimestamp = `${newDate}T${originalTime}`;

        await fetch(API_URL, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentId: tx.paymentId,
            paymentType: tx.paymentType,
            timestamp: updatedTimestamp,
            editingBranch: "ADMIN_SYSTEM",
          }),
        });
      }

      setMessage({
        text: "All connected transaction dates updated successfully!",
        type: "success",
      });
      handleSearch(); // Refresh the list
    } catch (err) {
      setMessage({ text: "Update failed during the process.", type: "error" });
    }
    setLoading(false);
  };

  return (
    <div className="admin-editor-page">
      <Helmet>
        <title>Admin | Date Editor</title>
      </Helmet>

      <header className="admin-header">
        <h2>
          <FaCalendarAlt /> Transaction Date Management
        </h2>
        <Link to="/">
          <button className="br-btn">Back</button>
        </Link>
      </header>

      <section className="search-section">
        <form onSubmit={handleSearch} className="admin-search-bar">
          <input
            type="text"
            placeholder="Enter Transaction ID (e.g., TX-1714...) "
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
          />
          <button type="submit" disabled={loading} className="btn-search">
            <FaSearch /> {loading ? "Searching..." : "Search"}
          </button>
        </form>
      </section>

      {message.text && (
        <div className={`admin-msg ${message.type}`}>{message.text}</div>
      )}

      {relatedTransactions.length > 0 && (
        <div className="update-controls">
          <div className="date-input-group">
            <label>Set New Date for ALL:</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
            <button
              onClick={handleUpdateDate}
              className="btn-update-all"
              disabled={loading || !newDate}
            >
              Update All Connected Records
            </button>
          </div>

          <div className="transaction-preview-list">
            <h3>Connected Transactions (The "Family")</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>ID</th>
                  <th>From/To</th>
                  <th>Current Date</th>
                  <th>Relation</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {relatedTransactions.map((tx) => (
                  <tr key={tx.paymentId}>
                    <td>
                      <span className={`pill ${tx.paymentType}`}>
                        {tx.paymentType}
                      </span>
                    </td>
                    <td>{tx.paymentId}</td>
                    <td>
                      {tx.fromBranch} → {tx.toBranch}
                    </td>
                    <td>{tx.timestamp?.split("T")[0] ?? "—"}</td>
                    <td>
                      {tx.linkedId ? <FaLink title="Linked Pair" /> : null}
                      {tx.paymentType?.includes("SPLIT") ? (
                        <FaLayerGroup title="Split Part" />
                      ) : null}
                    </td>
                    <td>{tx.amount}</td>
                    <td>
                      <span className={`pill ${tx.status}`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <AdminAuditReport allTransactions={transactions} />
    </div>
  );
}
